import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Edit, Trash2, CheckCircle, X, Calendar, Building, User, TrendingUp, FileText, RotateCcw, AlertCircle, Download } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Badge } from '@/components/common/Badge';
import { useToast } from '@/components/common/Toast';
import { PageHeader } from '@/components/common/PageHeader';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
} from '@/components/common/Dialog';
import { mqlService, MQL } from '@/services/api/mqlService';
import apiClient from '@/services/api/apiClient';
import { downloadMqlReport } from '../utils/mqlReport';

const STAGE_TITLES: Record<number, string> = {
  1: 'Account & Contact Context', 2: 'Maturity Mirror Exposure', 3: 'Role-Based Pain Framing',
  4: 'Maturity Gap Awareness', 5: 'MES Reality Alignment', 6: 'Alignment Call #1',
  7: 'Architecture Thinking', 8: 'Multi-System Coexistence', 9: 'Data Readiness',
  10: 'Automation Timing', 11: 'Decision Intelligence', 12: 'Confirmation Call #2',
  13: 'Future-State Snapshot', 14: 'MQL Validation & Handoff',
};

const statusColors = {
  PENDING: 'secondary',
  APPROVED: 'default',
  REJECTED: 'destructive',
  REVIEW: 'outline',
} as const;

export default function MQLDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [mql, setMql] = useState<MQL | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<MQL['status'] | null>(null);
  const [journey, setJourney] = useState<any | null>(null);
  const [account, setAccount] = useState<any | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [feedback, setFeedback] = useState<any[]>([]);
  const [feedbackText, setFeedbackText] = useState('');
  const [savingFeedback, setSavingFeedback] = useState(false);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      try {
        setLoading(true);
        const data = await mqlService.getMQLById(id);
        setMql(data);
        setFeedback(Array.isArray(data.feedbackLog) ? data.feedbackLog : []);
        if (data.contactId) {
          apiClient.get(`/api/abm-journeys/contact/${data.contactId}`)
            .then(r => { if (r.data && r.data.id) setJourney(r.data); }).catch(() => {});
        }
        if (data.accountId) {
          apiClient.get(`/api/accounts/${data.accountId}`)
            .then(r => { if (r.data) setAccount(r.data); }).catch(() => {});
        }
      } catch (error: any) {
        addToast({ type: 'error', title: 'Not Found', description: 'MQL submission could not be found.' });
        navigate('/mql');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const handleDelete = async () => {
    if (!mql) return;
    try {
      await mqlService.deleteMQL(mql.id);
      addToast({ type: 'success', title: 'MQL Deleted', description: `MQL for ${mql.contactName || mql.accountName} deleted.` });
      navigate('/mql');
    } catch (error: any) {
      addToast({ type: 'error', title: 'Delete Failed', description: error.message });
    }
  };

  const handleStatusChange = async () => {
    if (!mql || !pendingStatus) return;
    try {
      const reviewedBy = pendingStatus === 'APPROVED' ? 'Sales Manager' : undefined;
      const updated = await mqlService.updateStatus(mql.id, pendingStatus, reviewedBy);
      setMql(updated);
      addToast({ type: 'success', title: 'Status Updated', description: `MQL status changed to ${pendingStatus}.` });
      setStatusDialogOpen(false);
    } catch (error: any) {
      addToast({ type: 'error', title: 'Status Update Failed', description: error.message });
    }
  };

  const openStatusDialog = (status: MQL['status']) => {
    setPendingStatus(status);
    setStatusDialogOpen(true);
  };

  const handleDownload = async () => {
    if (!mql) return;
    setDownloading(true);
    try {
      await downloadMqlReport(mql);
    } catch (error: any) {
      addToast({ type: 'error', title: 'Download Failed', description: error?.message || 'Could not generate report.' });
    } finally {
      setDownloading(false);
    }
  };

  const handleAddFeedback = async () => {
    if (!mql || !feedbackText.trim()) return;
    setSavingFeedback(true);
    try {
      const r = await mqlService.addFeedback(mql.id, feedbackText.trim());
      setFeedback(r.feedbackLog || []);
      setFeedbackText('');
      addToast({ type: 'success', title: 'Feedback Added', description: 'Your note was saved to this MQL.' });
    } catch (error: any) {
      addToast({ type: 'error', title: 'Could Not Save', description: error?.response?.data?.error || error?.message || 'Failed to add feedback.' });
    } finally {
      setSavingFeedback(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading MQL details...</p>
        </div>
      </div>
    );
  }

  if (!mql) return null;

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  const getCriteriaMet = () => `${Object.values(mql.criteria).filter(Boolean).length}/4`;

  return (
    <div className="space-y-6">
      <PageHeader
        onBack={() => navigate('/mql')}
        title="MQL Details"
        description="View and manage Marketing Qualified Lead"
        actions={
          <>
            <Button variant="outline" onClick={handleDownload} disabled={downloading}>
              <Download className="h-4 w-4 mr-2" />{downloading ? 'Preparing…' : 'Download Report'}
            </Button>
            {mql.status === 'PENDING' && (
              <Button onClick={() => openStatusDialog('APPROVED')}>
                <CheckCircle className="h-4 w-4 mr-2" />Approve MQL
              </Button>
            )}
            {mql.status === 'PENDING' && (
              <Button variant="outline" onClick={() => openStatusDialog('REVIEW')}>
                <AlertCircle className="h-4 w-4 mr-2" />Mark for Review
              </Button>
            )}
            {(mql.status === 'APPROVED' || mql.status === 'REVIEW') && (
              <Button variant="outline" onClick={() => openStatusDialog('REJECTED')}>
                <X className="h-4 w-4 mr-2" />Reject
              </Button>
            )}
            {(mql.status === 'REJECTED') && (
              <Button variant="outline" onClick={() => openStatusDialog('REVIEW')}>
                <RotateCcw className="h-4 w-4 mr-2" />Re-open
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate(`/mql/${mql.id}/edit`)}>
              <Edit className="h-4 w-4 mr-2" />Edit
            </Button>
            <Button variant="destructive" onClick={() => setDeleteDialogOpen(true)}>
              <Trash2 className="h-4 w-4 mr-2" />Delete
            </Button>
          </>
        }
      />

      <div className="grid gap-6 md:grid-cols-3">
        <div className="md:col-span-2 space-y-6">
          <div className="border rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">Overview</h2>
              <Badge variant={statusColors[mql.status]}>{mql.status}</Badge>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="flex items-start gap-3">
                <Building className="h-5 w-5 mt-0.5 text-muted-foreground" />
                <div><p className="text-sm text-muted-foreground">Account</p><p className="font-medium">{mql.accountName}</p></div>
              </div>
              <div className="flex items-start gap-3">
                <User className="h-5 w-5 mt-0.5 text-muted-foreground" />
                <div><p className="text-sm text-muted-foreground">Contact</p><p className="font-medium">{mql.contactName || '\u2014'}</p></div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 mt-0.5 text-muted-foreground" />
                <div><p className="text-sm text-muted-foreground">Submitted Date</p><p className="font-medium">{formatDate(mql.submittedDate)}</p></div>
              </div>
              <div className="flex items-start gap-3">
                <TrendingUp className="h-5 w-5 mt-0.5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Lead Score</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-primary" style={{ width: `${mql.score}%` }} />
                    </div>
                    <span className="font-medium">{mql.score}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="border rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">BANT Criteria</h2>
              <span className="text-sm text-muted-foreground">{getCriteriaMet()} criteria met</span>
            </div>
            <div className="space-y-4">
              {[
                { key: 'budget', label: 'Budget', trueText: 'Has allocated budget', falseText: 'Budget not confirmed' },
                { key: 'authority', label: 'Authority', trueText: 'Has decision-making authority', falseText: 'Decision authority not confirmed' },
                { key: 'need', label: 'Need', trueText: 'Clear business need identified', falseText: 'Business need not confirmed' },
                { key: 'timeline', label: 'Timeline', trueText: 'Has defined timeline', falseText: 'Timeline not confirmed' },
              ].map(({ key, label, trueText, falseText }) => {
                const met = mql.criteria[key as keyof typeof mql.criteria];
                return (
                  <div key={key} className="flex items-center gap-3">
                    {met ? <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" /> : <X className="h-5 w-5 text-destructive flex-shrink-0" />}
                    <div>
                      <p className="font-medium">{label}</p>
                      <p className="text-sm text-muted-foreground">{met ? trueText : falseText}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {mql.notes && (
            <div className="border rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="h-5 w-5 text-muted-foreground" />
                <h2 className="text-xl font-semibold">Notes</h2>
              </div>
              <p className="text-muted-foreground whitespace-pre-wrap">{mql.notes}</p>
            </div>
          )}

          {Array.isArray((mql as any).stageRemarks) && (mql as any).stageRemarks.length > 0 && (
            <div className="border rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="h-5 w-5 text-muted-foreground" />
                <h2 className="text-xl font-semibold">Stage Remarks</h2>
              </div>
              <div className="space-y-4">
                {(mql as any).stageRemarks.map((sr: any) => (
                  <div key={sr.stage}>
                    <p className="text-sm font-semibold mb-1">Stage {sr.stage}</p>
                    <div className="space-y-1">
                      {(sr.remarks || []).map((r: any, i: number) => (
                        <div key={i} className="text-sm bg-muted/40 rounded px-3 py-1.5">
                          <span className="text-foreground/90">{r.note}</span>
                          <span className="text-muted-foreground text-xs"> — {r.by || 'user'}{r.at ? `, ${new Date(r.at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}` : ''}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="border rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Status Information</h2>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Current Status</p>
                <Badge variant={statusColors[mql.status]} className="mt-1">{mql.status}</Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Submitted By</p>
                <p className="font-medium">{mql.submittedBy}</p>
              </div>
              {mql.reviewedBy && (
                <div>
                  <p className="text-sm text-muted-foreground">Reviewed By</p>
                  <p className="font-medium">{mql.reviewedBy}</p>
                </div>
              )}
              {mql.salesAcceptedAt && (
                <div>
                  <p className="text-sm text-muted-foreground">Approved At</p>
                  <p className="font-medium">{formatDate(mql.salesAcceptedAt)}</p>
                </div>
              )}
              {mql.salesNotes && (
                <div>
                  <p className="text-sm text-muted-foreground">Sales Notes</p>
                  <p className="text-sm">{mql.salesNotes}</p>
                </div>
              )}
            </div>
          </div>

          {journey && (
            <div className="border rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">ABM Journey</h2>
                <Badge variant="outline">Stage {journey.currentStage} / 14</Badge>
              </div>
              <div className="mb-3">
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>{(journey.completedStages || []).length} of 14 completed</span>
                  <span>{Math.round(((journey.completedStages || []).length / 14) * 100)}%</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: `${Math.round(((journey.completedStages || []).length / 14) * 100)}%` }} />
                </div>
              </div>
              {account && (account.primaryPainAxis || account.maturityBand) && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {account.primaryPainAxis && <Badge variant="secondary">{account.primaryPainAxis}</Badge>}
                  {account.maturityBand && <Badge variant="outline">{account.maturityBand}</Badge>}
                </div>
              )}
              {account?.winTheme && (
                <div className="mb-3">
                  <p className="text-xs text-muted-foreground mb-0.5">Win Theme</p>
                  <p className="text-sm italic">{account.winTheme}</p>
                </div>
              )}
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {(journey.completedStages || []).slice().sort((a, b) => a - b).map((st) => {
                  const sd = journey.stageData || {};
                  const d = sd[st] || sd[String(st)] || {};
                  const line = d.activities || d.response || d.notes || d.nextSteps;
                  if (!line) return null;
                  return (
                    <div key={st} className="text-sm border-l-2 border-primary/30 pl-2">
                      <p className="font-medium text-xs">{st}. {STAGE_TITLES[st] || `Stage ${st}`}</p>
                      <p className="text-muted-foreground text-xs line-clamp-2">{line}</p>
                    </div>
                  );
                })}
                {(journey.completedStages || []).length === 0 && (
                  <p className="text-sm text-muted-foreground">No completed stages yet.</p>
                )}
              </div>
            </div>
          )}

          <div className="border rounded-lg p-6">
            <div className="flex items-center gap-2 mb-1">
              <FileText className="h-5 w-5 text-muted-foreground" />
              <h2 className="text-lg font-semibold">Sales / BD Feedback</h2>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              Log what happened with this contact and lead. Informational only — does not change the MQL status.
            </p>

            <div className="space-y-3 mb-4 max-h-80 overflow-y-auto">
              {feedback.length === 0 && (
                <p className="text-sm text-muted-foreground">No feedback yet. Add the first note below.</p>
              )}
              {feedback.slice().reverse().map((f: any) => (
                <div key={f.id} className="border-l-2 border-primary/30 pl-3 py-1">
                  <p className="text-sm whitespace-pre-wrap">{f.text}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {f.author || 'Unknown'} · {new Date(f.at).toLocaleString('en-US', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              ))}
            </div>

            <textarea
              className="w-full text-sm border rounded-md p-2 min-h-[80px] resize-y focus:outline-none focus:ring-2 focus:ring-primary/40"
              placeholder="e.g. Spoke to the contact — they've looped in their plant head, follow-up demo set for next week."
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              maxLength={5000}
            />
            <div className="flex justify-end mt-2">
              <Button onClick={handleAddFeedback} disabled={savingFeedback || !feedbackText.trim()}>
                {savingFeedback ? 'Saving…' : 'Add Feedback'}
              </Button>
            </div>
          </div>

          <div className="border rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
            <div className="space-y-2">
              <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/accounts')}>
                <Building className="h-4 w-4 mr-2" />View Accounts
              </Button>
              <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/contacts')}>
                <User className="h-4 w-4 mr-2" />View Contacts
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogClose onClose={() => setDeleteDialogOpen(false)} />
          <DialogHeader>
            <DialogTitle>Delete MQL</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this MQL for <span className="font-semibold">{mql.contactName || mql.accountName}</span>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete MQL</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent>
          <DialogClose onClose={() => setStatusDialogOpen(false)} />
          <DialogHeader>
            <DialogTitle>Confirm Status Change</DialogTitle>
            <DialogDescription>
              Change MQL status to <span className="font-semibold">{pendingStatus}</span>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleStatusChange}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
